import React, { useState, useEffect, useMemo } from "react";
import {
  Route,
  Navigate,
  Routes,
  useNavigate,
  Link,
  useParams,
} from "react-router-dom";
import ChatSidebar from "./components/ChatSidebar";
import Chat from "views/agent/chat/Chat";
import { Message } from "views/agent/chat/types";
import Loader from "components/Loader";
import Card from "components/card";
import useAgents from "./hooks/useAgents";
import useConversations from "./hooks/useConversations";
import { useAuthContext } from "auth/AuthProvider";
import { PERMISSIONS } from "auth/permissions";

export interface Conversation {
  id: string;
  name: string;
  agent_id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

const AgentChat = () => {
  const navigate = useNavigate();
  const { "*": prefix } = useParams();
  const agentId = prefix.split("/")[0];
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);

  // Custom hooks for agents and conversations
  const { availableAgents, isLoadingAgents, agentsError, fetchAgents } =
    useAgents();

  const {
    conversations,
    totalConversations,
    isLoadingConversations,
    conversationsError,
    fetchConversations,
    handleLoadMoreConversations,
  } = useConversations();

  const currentAgent = useMemo(
    () => availableAgents.find((a) => a.id === agentId),
    [availableAgents, agentId]
  );

  const { hasPermission } = useAuthContext();

  const permissions = {
    canCreateAgent: hasPermission(PERMISSIONS.CREATE_AGENT),
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleConversationClick = (conversationId: string, agentId: string) => {
    setOpen(false);
    navigate(`/dashboard/chat/${agentId}/${conversationId}`, {
      replace: true,
    });
  };

  const handleAgentSelect = (agentId: string) => {
    setOpen(false);
    setMessages([]);
    navigate(`/dashboard/chat/${agentId}`, { replace: true });
  };

  const renderMainContent = () => {
    if (isLoadingAgents) {
      return (
        <Loader
          txt="Loading..."
          className="flex h-full items-center justify-center "
        />
      );
    }

    if (availableAgents.length > 0) {
      return (
        <Navigate to={`/dashboard/chat/${availableAgents[0].id}`} replace />
      );
    }
    return (
      <div className="flex h-full items-center justify-center">
        {permissions.canCreateAgent ? (
          <Link
            to="/dashboard/agents/create"
            className="text-lg font-medium text-navy-700 hover:text-navy-900 dark:text-white dark:hover:text-gray-200"
          >
            No agents available. Click here to create your first agent.
          </Link>
        ) : (
          <p className="text-lg font-medium text-navy-700 dark:text-white">
            No agents available.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="relative mt-10 flex h-[88.7vh] w-full flex-col gap-4 p-4 sm:mt-0 lg:flex-row">
      <ChatSidebar
        onLoadMore={handleLoadMoreConversations}
        conversations={conversations}
        availableAgents={availableAgents}
        isLoadingConversations={isLoadingConversations}
        onConversationClick={handleConversationClick}
        canContinue={totalConversations > conversations.length}
        onAgentSelect={handleAgentSelect}
        selectedAgentId={agentId?.split("/")[0]}
        isLoadingAgents={isLoadingAgents}
        conversationsError={conversationsError}
        agentsError={agentsError}
        onRetryConversations={fetchConversations}
        onRetryAgents={fetchAgents}
        open={open}
        setOpen={setOpen}
      />

      <Card extra="min-w-0 flex-1  sm:mt-0">
        <Routes>
          <Route path="/" element={renderMainContent()} />
          <Route
            path=":agentId"
            element={
              <Chat
                fetchConversations={fetchConversations}
                currentAgent={currentAgent}
                open={open}
                onClose={() => setOpen(!open)}
                setMessages={setMessages}
                messages={messages}
              />
            }
          />
          <Route
            path=":agentId/:conversationId"
            element={
              <Chat
                open={open}
                currentAgent={currentAgent}
                onClose={() => setOpen(!open)}
                setMessages={setMessages}
                messages={messages}
              />
            }
          />
        </Routes>
      </Card>
    </div>
  );
};

export default AgentChat;
